import { CURRENT_SCHEMA_VERSION, restoreDesign } from "./migrations";
import { ensureNodeBlockTypes, GameState } from "../models/game";

// Each interview problem keeps its own autosaved attempt, separate from the
// scratchpad and named designs, so switching problems never loses work.
const PROBLEM_ATTEMPTS_KEY = "problemAttempts";

/** Fired on window whenever an attempt is created or deleted. */
export const PROBLEM_ATTEMPTS_CHANGED_EVENT = "problemAttemptsChanged";

export interface ProblemAttempt {
  version: number;
  state: GameState;
  startedAt: string;
  updatedAt: string;
}

export type ProblemAttempts = Record<string, ProblemAttempt>;

export const getProblemAttempts = (): ProblemAttempts => {
  try {
    const raw = localStorage.getItem(PROBLEM_ATTEMPTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const setProblemAttempts = (attempts: ProblemAttempts) => {
  try {
    localStorage.setItem(PROBLEM_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch {
    // ignore write errors (quota, private mode)
  }
};

const notifyChanged = () => {
  try {
    window.dispatchEvent(new Event(PROBLEM_ATTEMPTS_CHANGED_EVENT));
  } catch {
    // ignore
  }
};

export const getProblemAttempt = (problemId: string): ProblemAttempt | undefined =>
  getProblemAttempts()[problemId];

export const hasProblemAttempt = (problemId: string): boolean =>
  getProblemAttempt(problemId) !== undefined;

/** Create or update a problem's attempt with the given game state. */
export const saveProblemAttempt = (problemId: string, state: GameState) => {
  const attempts = getProblemAttempts();
  const existing = attempts[problemId];
  const now = new Date().toISOString();
  attempts[problemId] = {
    version: CURRENT_SCHEMA_VERSION,
    state,
    startedAt: existing?.startedAt ?? now,
    updatedAt: now,
  };
  setProblemAttempts(attempts);
  if (!existing) notifyChanged();
};

export const deleteProblemAttempt = (problemId: string) => {
  const attempts = getProblemAttempts();
  if (!(problemId in attempts)) return;
  delete attempts[problemId];
  setProblemAttempts(attempts);
  notifyChanged();
};

/**
 * Restore a problem's attempt for loading into the store. The simulation
 * interval lives in a hook, so a resumed attempt always comes back paused.
 */
export const loadProblemAttemptState = (problemId: string): GameState | undefined => {
  const attempt = getProblemAttempt(problemId);
  const state = restoreDesign(attempt);
  if (!state || typeof state !== "object") return undefined;
  return ensureNodeBlockTypes({
    ...state,
    isRunning: false,
    isDraggingNode: false,
    activeProblemId: problemId,
  });
};
